import { Divider } from '@unifyapps/ui/components/Divider';
import { Stack } from '@unifyapps/ui/components/Stack';
import _debounce from 'lodash/debounce';
import { useCallback, useMemo } from 'react';
import { Virtuoso } from 'react-virtuoso';
import { useUserStore } from '../../../auth/UserStoreProvider';
import { useUserInboxSubscription } from '../hooks/useUserInboxSubscription';
import type { Notification as NotificationType } from '../types';
import { NotificationItem } from './NotificationItem';

type Props = {
  items: NotificationType[];
  fetchNextPage?: () => void;
  scrollParent?: HTMLDivElement;
  refetch: () => Promise<unknown>;
  onMarkAsRead?: (id: string) => void;
};

export function Notifications({
  items: notifications,
  fetchNextPage,
  scrollParent,
  refetch,
  onMarkAsRead,
}: Props) {
  const userData = useUserStore().hooks.useCurrentUserDetails();
  const loggedInUserId = userData?.user?.id;

  const onNotificationReceive = useMemo(
    () =>
      _debounce(() => {
        void refetch();
      }, 300),
    [refetch],
  );

  useUserInboxSubscription({
    loggedInUserId,
    onNotificationReceive,
    topics: userData?.notificationMqttTopics,
  });

  const fetchMoreOnBottomReached = useCallback(
    (atBottom: boolean) => {
      if (fetchNextPage && atBottom) {
        fetchNextPage();
      }
    },
    [fetchNextPage],
  );

  const itemContent = useCallback(
    (index: number) => (
      <Stack direction="column">
        <NotificationItem
          className="py-lg px-md rounded-md"
          notification={notifications[index]}
          onMarkAsRead={onMarkAsRead}
        />
        <Divider className="mx-sm" orientation="horizontal" />
      </Stack>
    ),
    [notifications, onMarkAsRead],
  );

  return (
    <Virtuoso
      atBottomStateChange={fetchMoreOnBottomReached}
      atBottomThreshold={600}
      customScrollParent={scrollParent}
      itemContent={itemContent}
      totalCount={notifications.length}
    />
  );
}
